import { LscApiError } from './api';
import type {
  ScheduleRepairDiffSummary,
  ScheduleRepairStructuredErrorCode,
  ScheduleRepairStatus,
} from './types';
import { parseLscMutationError, type ParsedLscMutationError } from './entitlementBillingErrors';
import type { CanonicalScheduleContext } from './leagueScheduleContext';
import { buildInternalCopyPattern } from './internalSanitizerPatterns';

export const SCHEDULE_REPAIR_DETECTED_TITLE = 'Schedule repair needed';
export const SCHEDULE_REPAIR_DETECTED_COPY =
  'LSC detected a schedule mismatch. Preview the proposed repair, review the changes, and apply only when you are ready.';
export const SCHEDULE_REPAIR_PREVIEW_CTA = 'Preview schedule repair';
export const SCHEDULE_REPAIR_APPLY_CTA = 'Apply schedule repair';
export const SCHEDULE_REPAIR_APPLY_WARNING =
  'This updates your league games from the reviewed repair preview. Nothing is applied automatically.';
export const SCHEDULE_REPAIR_PREVIEW_READY_TITLE = 'Schedule repair preview';
export const SCHEDULE_REPAIR_REVIEW_REQUIRED_COPY =
  'This repair needs commissioner review before it can be applied.';
export const SCHEDULE_REPAIR_CONFLICTS_COPY =
  'Team assignment conflicts must be resolved before schedule repair can be applied.';
export const SCHEDULE_REPAIR_SUCCESS_TITLE = 'Schedule repair applied';
export const SCHEDULE_REPAIR_SUCCESS_COPY =
  'Your league schedule was updated from the reviewed repair preview.';
export const SCHEDULE_REPAIR_NO_CHANGES_COPY =
  'No schedule changes were detected for repair.';
export const SCHEDULE_REPAIR_NOT_CONFIGURED_COPY =
  'Connect a schedule source or import a schedule before previewing repair.';
export const SCHEDULE_REPAIR_SOURCE_UNAVAILABLE_COPY =
  'Schedule data is not ready for repair yet. Import or refresh your schedule, then try again.';
export const SCHEDULE_REPAIR_STALE_PREVIEW_COPY =
  'This repair preview expired or is no longer valid. Preview again before applying.';
export const SCHEDULE_REPAIR_GAMES_BANNER_COPY =
  'Your active schedule labels may not match the games shown here. Review schedule repair before relying on this view.';
export const SCHEDULE_REPAIR_GAMES_BANNER_CTA = 'Review schedule repair';
export const SCHEDULE_REPAIR_SETUP_LINK_COPY =
  'When schedule repair is needed, use the preview below to review changes before applying.';

const SCHEDULE_REPAIR_ERROR_COPY: Record<ScheduleRepairStructuredErrorCode, string> = {
  schedule_repair_not_configured: SCHEDULE_REPAIR_NOT_CONFIGURED_COPY,
  schedule_repair_source_unavailable: SCHEDULE_REPAIR_SOURCE_UNAVAILABLE_COPY,
  schedule_repair_preview_required:
    'Run a schedule repair preview before applying changes.',
  schedule_repair_conflicts_require_review: SCHEDULE_REPAIR_CONFLICTS_COPY,
  schedule_repair_forbidden: 'You do not have permission to repair this schedule.',
  schedule_repair_league_mismatch:
    'This repair preview belongs to a different league. Preview again from this league.',
  schedule_repair_stale_preview: SCHEDULE_REPAIR_STALE_PREVIEW_COPY,
  schedule_repair_no_changes: SCHEDULE_REPAIR_NO_CHANGES_COPY,
  schedule_repair_invalid_context:
    'Schedule repair context is no longer valid. Preview again before applying.',
  schedule_repair_not_found: 'Schedule repair preview was not found. Preview again before applying.',
};

const INTERNAL_COPY_PATTERN = buildInternalCopyPattern(
  'firestore|snapshot_id|repair_run|operator|env_|__ENV_PREFIX__|provider payload|raw payload'
);

export function scheduleRepairErrorMessage(
  code: string | undefined,
  fallbackMessage?: string
): string {
  if (code && code in SCHEDULE_REPAIR_ERROR_COPY) {
    return SCHEDULE_REPAIR_ERROR_COPY[code as ScheduleRepairStructuredErrorCode];
  }
  if (fallbackMessage && !INTERNAL_COPY_PATTERN.test(fallbackMessage)) {
    return fallbackMessage;
  }
  return 'Schedule repair could not be completed. Try previewing again.';
}

export function parseScheduleRepairError(
  err: unknown,
  fallback = 'Schedule repair failed.'
): ParsedLscMutationError {
  if (err instanceof LscApiError && err.code) {
    const message = scheduleRepairErrorMessage(err.code, err.message);
    const base = parseLscMutationError(err, message);
    return { ...base, message };
  }
  return parseLscMutationError(err, fallback);
}

export function formatScheduleRepairDiffSummary(summary: ScheduleRepairDiffSummary): string[] {
  const lines: string[] = [];
  if (summary.games_to_add > 0) {
    lines.push(`${summary.games_to_add} game${summary.games_to_add === 1 ? '' : 's'} to add`);
  }
  if (summary.games_to_update > 0) {
    lines.push(`${summary.games_to_update} game${summary.games_to_update === 1 ? '' : 's'} to update`);
  }
  if (summary.games_to_remove > 0) {
    lines.push(`${summary.games_to_remove} game${summary.games_to_remove === 1 ? '' : 's'} to remove`);
  }
  if (summary.games_to_ignore > 0) {
    lines.push(`${summary.games_to_ignore} game${summary.games_to_ignore === 1 ? '' : 's'} ignored`);
  }
  if (summary.conflicts > 0) {
    lines.push(
      `${summary.conflicts} conflict${summary.conflicts === 1 ? '' : 's'} need${summary.conflicts === 1 ? 's' : ''} review`
    );
  }
  return lines;
}

export function scheduleRepairStatusHeadline(status: ScheduleRepairStatus | null): string {
  if (!status?.repair_needed) {
    return 'Schedule repair is up to date';
  }
  if (status.recommended_action === 'connect_source') {
    return 'Schedule source required';
  }
  if (status.recommended_action === 'review_conflicts') {
    return 'Schedule conflicts need review';
  }
  return SCHEDULE_REPAIR_DETECTED_TITLE;
}

export function scheduleRepairStatusDetail(status: ScheduleRepairStatus | null): string {
  if (!status?.repair_needed) {
    return 'No schedule repair is needed right now.';
  }
  if (status.recommended_action === 'connect_source') {
    return SCHEDULE_REPAIR_NOT_CONFIGURED_COPY;
  }
  if (status.recommended_action === 'review_conflicts') {
    return SCHEDULE_REPAIR_CONFLICTS_COPY;
  }
  return SCHEDULE_REPAIR_DETECTED_COPY;
}

export function canonicalScheduleContextNeedsRepair(context: CanonicalScheduleContext): boolean {
  if (!context.available) {
    return false;
  }
  if (context.provenance === 'latest_match_season_fallback') {
    return true;
  }
  if (context.provenance === 'league_active_season_derived_stage') {
    return true;
  }
  return context.reason_codes.some((code) =>
    [
      'stale_league_active_season',
      'league_active_season_without_matches',
      'league_active_stage_without_matches',
      'league_active_stage_outside_canonical_season',
      'missing_league_active_season',
      'missing_league_active_stage',
    ].includes(code)
  );
}

export function assertCustomerSafeScheduleRepairCopy(text: string): boolean {
  return !INTERNAL_COPY_PATTERN.test(text);
}
